import { useTranslation } from "react-i18next";
import Loader from "../../../Loader";
import { Switch } from "../../../ui/Switch";
import { pushSettingsView } from "../../../../domains/app/stores/settingsSheetStore";
import { usePartnerSharing } from "../../../../domains/partners/usePartnerSharing";
import { cn } from "../../../../lib/utils";
import { SettingsSubHeader } from "../SettingsSubHeader";
import { SettingsSectionLabel } from "../SettingsSectionLabel";

/**
 * "Partner sharing" (Figma O4, docs/partner-objection.md REQ-09): one switch
 * for all partners, present and future, then one switch for each partner of
 * the rooms of the user. A switch shows the state: on, the partner receives
 * the email address; off, an objection applies. A partner that owns two rooms
 * has one switch, so turning it off covers both (AC-09).
 */
export function PartnerSharingView(): React.JSX.Element {
  const { t } = useTranslation();
  const { sharingQuery, partnersQuery, isSaving, changeSharing } =
    usePartnerSharing();

  return (
    <div className="flex h-full flex-col">
      <SettingsSubHeader title={t("Partner sharing")} />
      <div className="flex-1 overflow-y-auto scroll-fog-y px-5 pb-12">
        {sharingQuery.state === "pending" ? (
          <div className="flex justify-center py-10">
            <Loader />
          </div>
        ) : sharingQuery.state === "error" ? (
          <p className="text-ui-body text-ds-slate-600">
            {t("Something went wrong. Please try again.")}
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            <p className="m-0 text-ui-body text-ds-slate-700">
              {t(
                "When you join a room a partner supports, the partner receives your email address and may contact you for professional communications. Turning sharing off doesn't change your access to content."
              )}
            </p>

            <div className="flex items-center gap-3 rounded-xl bg-ds-slate-50 p-4">
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="text-ui-body font-medium text-ds-slate-900">
                  {t("Share my email with partners")}
                </span>
                <span className="text-footnote text-ds-slate-700">
                  {t(
                    "All partners, including future ones. Turn off to object."
                  )}
                </span>
              </div>
              <Switch
                checked={sharingQuery.body.sharing}
                label={t("Share my email with partners")}
                disabled={isSaving}
                onCheckedChange={(sharing) => {
                  changeSharing(null, sharing);
                }}
              />
            </div>

            <div className="flex items-center gap-2 text-footnote font-medium">
              <button
                type="button"
                className="cursor-pointer border-none bg-transparent p-0 text-ds-mint-700"
                onClick={() => {
                  pushSettingsView({ view: "partners" });
                }}
              >
                {partnersQuery.state === "success"
                  ? t("See our {{n}} partners", {
                      n: partnersQuery.body.length,
                    })
                  : t("See our partners")}
              </button>
              <span className="text-ds-slate-700">·</span>
              <button
                type="button"
                className="cursor-pointer border-none bg-transparent p-0 text-ds-mint-700"
                onClick={() => {
                  pushSettingsView({ view: "juisciPrivacy" });
                }}
              >
                {t("Privacy policy")}
              </button>
            </div>

            {sharingQuery.body.partners.length > 0 && (
              <div className="flex flex-col gap-1">
                <SettingsSectionLabel>
                  {t("PARTNER BY PARTNER")}
                </SettingsSectionLabel>
                <p className="m-0 text-footnote text-ds-slate-700">
                  {t(
                    "Turn a partner off to stop sharing your email address with it. You stay a member of its rooms."
                  )}
                </p>
                <ul className="m-0 flex list-none flex-col p-0">
                  {sharingQuery.body.partners.map((partner) => (
                    <li
                      key={partner.organisationId}
                      className={cn(
                        "flex items-center gap-3 border-b border-ds-slate-100 py-3 pr-1",
                        !sharingQuery.body.sharing && "opacity-50"
                      )}
                    >
                      <span className="flex size-9.5 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-ds-moss-50 p-1">
                        <img
                          src={partner.logoUrl}
                          alt=""
                          className="max-h-full max-w-full object-contain"
                        />
                      </span>
                      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <span className="text-ui-body font-medium text-ds-slate-900">
                          {partner.name}
                        </span>
                        <span className="text-footnote text-ds-slate-700">
                          {t("Via {{rooms}}", {
                            rooms: partner.roomNames.join(", "),
                          })}
                        </span>
                      </span>
                      <Switch
                        checked={partner.sharing}
                        label={partner.name}
                        // The global switch off objects for every partner: the
                        // partner rows wait for it (Figma O4, off for all).
                        disabled={!sharingQuery.body.sharing || isSaving}
                        onCheckedChange={(sharing) => {
                          changeSharing(partner.organisationId, sharing);
                        }}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="m-0 text-footnote text-ds-slate-700">
              {t(
                "You stay a member of your rooms and keep access to their content."
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
